import React, { useState, useEffect } from 'react';
import {
  FiUserPlus,
  FiUsers,
  FiCheck,
  FiX,
  FiTrash2,
  FiSend,
  FiInbox,
  FiUser,
  FiSearch,
  FiAlertCircle,
  FiUserCheck,
  FiClock,
  FiShare2,
  FiShield,
  FiAward,
  FiRefreshCw,
  FiUserMinus
} from 'react-icons/fi';
import { showToast } from './NotificationTray';
import { useAuth } from '../contexts/AuthContext';
import { useTeam } from '../contexts/TeamContext';

export default function FriendsManager({ onClose }) {
  const { user, auth, isAdmin } = useAuth();
  const {
    teamMembers,
    loading: teamLoading,
    error: teamError,
    isTeamOwner,
    isTeamAdmin,
    syncTeam,
    updateMemberRole
  } = useTeam();
  const [activeTab, setActiveTab] = useState('friends');
  const [friends, setFriends] = useState([]);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searchResult, setSearchResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [syncingTeam, setSyncingTeam] = useState(false);
  const [archivingMemberId, setArchivingMemberId] = useState(null);

  useEffect(() => {
    loadFriends();
    loadRequests();
  }, []);

  async function loadFriends() {
    try {
      const res = await fetch('/api/friends', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setFriends(data);
      }
    } catch (error) {
      console.error('Failed to load friends:', error);
    }
  }

  async function loadRequests() {
    setLoadingRequests(true);
    try {
      const [incomingRes, outgoingRes] = await Promise.all([
        fetch('/api/friends/requests?direction=incoming', { credentials: 'include' }),
        fetch('/api/friends/requests?direction=outgoing', { credentials: 'include' })
      ]);

      if (incomingRes.ok) {
        const data = await incomingRes.json();
        setIncomingRequests(data);
      }

      if (outgoingRes.ok) {
        const data = await outgoingRes.json();
        setOutgoingRequests(data);
      }
    } catch (error) {
      console.error('Failed to load requests:', error);
    } finally {
      setLoadingRequests(false);
    }
  }

  // Autocomplete search for suggestions
  async function fetchSuggestions(query) {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      const res = await fetch(`/api/friends/search?q=${encodeURIComponent(query)}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const users = await res.json();
        // Filter out existing friends and users with pending requests
        const filtered = users.filter(u => {
          const isFriend = friends.some(f => f.id === u.id);
          const hasPendingRequest = outgoingRequests.some(r => r.to_user_id === u.id);
          return !isFriend && !hasPendingRequest;
        });
        setSuggestions(filtered.slice(0, 5));
      }
    } catch (error) {
      console.error('Autocomplete failed:', error);
    }
  }

  // Handle search input change with debounced autocomplete
  function handleSearchChange(value) {
    setSearchQuery(value);
    setSearchResult(null);

    // Debounce autocomplete
    clearTimeout(window.friendSearchTimeout);
    window.friendSearchTimeout = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
  }

  async function searchUser() {
    if (!searchQuery.trim()) return;

    setLoading(true);
    setSearchResult(null);
    setSuggestions([]);

    try {
      const res = await fetch(`/api/friends/lookup?q=${encodeURIComponent(searchQuery.trim())}`, {
        credentials: 'include'
      });

      if (res.ok) {
        const user = await res.json();

        // Check if already friends or request exists
        const isFriend = friends.some(f => f.email === user.email || f.id === user.id);
        const hasPendingRequest = outgoingRequests.some(r => r.to_email === user.email && r.status === 'pending');
        const hasIncomingRequest = incomingRequests.some(r => r.from_email === user.email && r.status === 'pending');

        setSearchResult({
          ...user,
          isFriend,
          hasPendingRequest,
          hasIncomingRequest
        });
      } else if (res.status === 404) {
        setSearchResult({ notFound: true });
      }
    } catch (error) {
      console.error('Search failed:', error);
      showToast('Failed to search for user', 'error');
    } finally {
      setLoading(false);
    }
  }

  function selectSuggestion(user) {
    setSearchQuery(user.username || user.email);
    setSuggestions([]);
    // Show user result directly
    const isFriend = friends.some(f => f.email === user.email || f.id === user.id);
    const hasPendingRequest = outgoingRequests.some(r => r.to_email === user.email && r.status === 'pending');
    const hasIncomingRequest = incomingRequests.some(r => r.from_email === user.email && r.status === 'pending');
    setSearchResult({
      ...user,
      isFriend,
      hasPendingRequest,
      hasIncomingRequest
    });
  }

  async function sendFriendRequest(email) {
    setSendingRequest(true);
    try {
      const res = await fetch('/api/friends/requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ to_email: email })
      });

      const data = await res.json();

      if (res.ok) {
        showToast('Friend request sent!', 'success');
        setSearchQuery('');
        setSearchResult(null);
        setSuggestions([]);
        loadRequests();
      } else if (res.status === 409) {
        showToast('Already friends with this user', 'info');
      } else {
        showToast(data.message || 'Failed to send request', 'error');
      }
    } catch (error) {
      console.error('Failed to send request:', error);
      showToast('Failed to send friend request', 'error');
    } finally {
      setSendingRequest(false);
    }
  }

  function getOutgoingRequest(user) {
    if (!user) return null;
    const email = (user.email || '').toLowerCase();
    return outgoingRequests.find(r =>
      (r.to_user_id && r.to_user_id === user.id) ||
      (r.to_email && email && r.to_email.toLowerCase() === email)
    ) || null;
  }

  async function cancelOutgoingRequest(requestId) {
    if (!requestId) return;
    if (!confirm('Cancel this friend request?')) return;

    try {
      const res = await fetch(`/api/friends/requests/${requestId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        showToast('Friend request canceled', 'info');
        setSearchResult(prev => prev && prev.hasPendingRequest ? { ...prev, hasPendingRequest: false } : prev);
        loadRequests();
        return;
      }
      const data = await res.json().catch(() => ({}));
      showToast(data.message || 'Failed to cancel request', 'error');
    } catch (error) {
      console.error('Failed to cancel request:', error);
      showToast('Failed to cancel request', 'error');
    }
  }

  async function handleRequest(requestId, action) {
    try {
      const res = await fetch(`/api/friends/requests/${requestId}/${action}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (res.ok) {
        showToast(
          action === 'accept' ? 'Friend request accepted!' : 'Friend request declined',
          action === 'accept' ? 'success' : 'info'
        );
        loadFriends();
        loadRequests();
      }
    } catch (error) {
      console.error(`Failed to ${action} request:`, error);
      showToast(`Failed to ${action} request`, 'error');
    }
  }

  async function removeFriend(friendId) {
    if (!confirm('Are you sure you want to remove this friend?')) return;

    try {
      const res = await fetch(`/api/friends/${friendId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        showToast('Friend removed', 'info');
        loadFriends();
      }
    } catch (error) {
      console.error('Failed to remove friend:', error);
      showToast('Failed to remove friend', 'error');
    }
  }

  function getTeamRole(member) {
    return member.role || (Array.isArray(member.roles) ? member.roles[0] : member.roles) || 'member';
  }

  function formatTeamDate(value) {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString();
  }

  async function handleTeamSync() {
    if (!syncTeam) return;
    setSyncingTeam(true);
    try {
      await syncTeam();
      showToast('Team synced from Core', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to sync team', 'error');
    } finally {
      setSyncingTeam(false);
    }
  }

  async function handleRoleChange(memberId, role) {
    if (!updateMemberRole) return;
    try {
      await updateMemberRole(memberId, role);
      showToast('Role updated', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to update role', 'error');
    }
  }

  async function archiveTeamMember(member) {
    if (!auth?.makeAuthenticatedRequest) return;
    const memberId = member?.user_id || member?.id;
    if (!memberId) return;
    const tenantContext = auth?.tenantContext;
    if (!tenantContext?.id) {
      showToast('Missing tenant context. Please sign in again.', 'error');
      return;
    }
    if (memberId === user?.id) {
      showToast('You cannot archive your own account.', 'error');
      return;
    }
    const confirmed = window.confirm(`Archive ${member.email}? They will no longer be able to sign in.`);
    if (!confirmed) return;

    setArchivingMemberId(memberId);
    try {
      const suspendRes = await auth.makeAuthenticatedRequest(
        `${auth.baseUrl}/api/auth-users/${memberId}/suspend`,
        { method: 'PUT' }
      );
      if (!suspendRes.ok) {
        const data = await suspendRes.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to archive member');
      }

      await auth.makeAuthenticatedRequest(
        `${auth.baseUrl}/tenants/${tenantContext.id}/users/${memberId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'suspended' })
        }
      ).catch(() => {});

      showToast('Team member archived', 'success');
      await syncTeam?.();
    } catch (err) {
      showToast(err.message || 'Failed to archive member', 'error');
    } finally {
      setArchivingMemberId(null);
    }
  }

  const pendingIncomingCount = incomingRequests.filter(r => r.status === 'pending').length;
  const pendingOutgoingCount = outgoingRequests.filter(r => r.status === 'pending').length;
  const totalFriends = friends.length;
  const totalRequests = pendingIncomingCount + pendingOutgoingCount;
  const totalTeamMembers = teamMembers.length;
  const canManageTeam = isTeamOwner || isTeamAdmin || (typeof isAdmin === 'function' && isAdmin());
  const canShowSearchAvatar = !!(searchResult?.profile_photo_key
    && (searchResult.isFriend || searchResult.hasPendingRequest || searchResult.hasIncomingRequest));

  return (
    <div className="friends-manager">
      {onClose && (
        <div className="modal-header">
          <h2><FiUsers className="inline-icon" /> Friends</h2>
          <button onClick={onClose} className="close-button">
            <FiX />
          </button>
        </div>
      )}

      <div className="friends-hero">
        <div className="hero-left">
          <p className="hero-kicker">Friends + team</p>
          <h3>Keep your inner circle and team aligned</h3>
          <p className="hero-copy">
            Invite friends, manage team roles, and share albums without public links.
          </p>
          <div className="hero-chips">
            <span className="chip">Friends · {totalFriends}</span>
            <span className="chip">Team · {totalTeamMembers}</span>
            <span className="chip">Incoming · {pendingIncomingCount}</span>
            <span className="chip">Sent · {pendingOutgoingCount}</span>
          </div>
        </div>
        <div className="hero-stats">
          <div className="stat-card">
            <span className="stat-label">Friends</span>
            <span className="stat-value">{totalFriends}</span>
            <span className="stat-hint">Trusted contacts</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Team</span>
            <span className="stat-value">{totalTeamMembers}</span>
            <span className="stat-hint">Collaborators</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Pending</span>
            <span className="stat-value">{totalRequests}</span>
            <span className="stat-hint">Incoming / Sent</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Requests sent</span>
            <span className="stat-value">{pendingOutgoingCount}</span>
            <span className="stat-hint">Awaiting acceptance</span>
          </div>
        </div>
      </div>

      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'friends' ? 'active' : ''}`}
          onClick={() => setActiveTab('friends')}
        >
          <FiUsers />
          Friends ({friends.length})
        </button>
        <button 
          className={`tab ${activeTab === 'team' ? 'active' : ''}`}
          onClick={() => setActiveTab('team')}
        >
          <FiShield />
          Team ({teamMembers.length})
        </button>
        <button 
          className={`tab ${activeTab === 'requests' ? 'active' : ''}`}
          onClick={() => setActiveTab('requests')}
        >
          <FiInbox />
          Requests 
          {pendingIncomingCount > 0 && (
            <span className="badge">{pendingIncomingCount}</span>
          )}
        </button>
        <button 
          className={`tab ${activeTab === 'add' ? 'active' : ''}`}
          onClick={() => setActiveTab('add')}
        >
          <FiUserPlus />
          Add Friend
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'friends' && (
          <div className="friends-list">
            {friends.length === 0 ? (
              <div className="empty-state">
                <FiUsers className="empty-icon" />
                <p>No friends yet</p>
                <p className="muted">Add friends to share albums privately</p>
              </div>
            ) : (
              <div className="friends-grid">
                {friends.map(friend => (
                  <div key={friend.id} className="friend-card">
                    <div
                      className={`friend-avatar ${friend.profile_photo_key ? 'has-photo' : ''}`}
                      style={friend.profile_photo_key ? { backgroundImage: `url(/api/profile/photo/${friend.id})` } : undefined}
                    >
                      {!friend.profile_photo_key && <FiUser />}
                    </div>
                    <div className="friend-info">
                      <div className="friend-name">{friend.username || friend.email.split('@')[0]}</div>
                      <div className="friend-email">{friend.email}</div>
                    </div>
                    <div className="friend-actions">
                      <button 
                        className="icon-button share-button"
                        title="Share album with friend"
                        onClick={() => {
                          // TODO: Open share modal with friend pre-selected
                          showToast('Share feature coming soon!', 'info');
                        }}
                      >
                        <FiShare2 />
                      </button>
                      <button 
                        className="icon-button danger"
                        title="Remove friend"
                        onClick={() => removeFriend(friend.id)}
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'team' && (
          <div className="team-section">
            <div className="team-header">
              <div>
                <h3>Team Members</h3>
                <p className="muted">Manage internal collaborators and their roles.</p>
              </div>
              {isTeamOwner && (
                <button
                  className="button-secondary small"
                  onClick={handleTeamSync}
                  disabled={syncingTeam}
                >
                  <FiRefreshCw className={syncingTeam ? 'animate-spin' : ''} />
                  {syncingTeam ? 'Syncing...' : 'Sync from Core'}
                </button>
              )}
            </div>

            {teamLoading && <div className="loading-state">Loading team members...</div>}
            {!teamLoading && teamError && (
              <div className="loading-state">Unable to load team members.</div>
            )}
            {!teamLoading && !teamError && (
              <>
                {teamMembers.length === 0 ? (
                  <div className="empty-state">
                    <FiUsers className="empty-icon" />
                    <p>No team members yet</p>
                    <p className="muted">Sync from Core to import your team.</p>
                  </div>
                ) : (
                  <div className="friends-grid">
                    {teamMembers.map(member => {
                      const memberId = member.user_id || member.id;
                      const role = getTeamRole(member);
                      const isSelf = memberId === user?.id;
                      const lastSynced = formatTeamDate(member.last_synced_at);
                      const canEditRole = isTeamOwner && !isSelf;
                      const canArchive = canManageTeam && !isSelf;

                      return (
                        <div key={memberId} className="friend-card team-card">
                          <div className="friend-avatar">
                            {role === 'owner' ? <FiAward /> : role === 'admin' ? <FiShield /> : <FiUser />}
                          </div>
                          <div className="friend-info">
                            <div className="friend-name">
                              {member.display_name || member.username || member.email?.split('@')[0]}
                              {isSelf && <span className="team-badge">you</span>}
                            </div>
                            <div className="friend-email">{member.email || ', '}</div>
                            <div className="team-meta">
                              {!canEditRole && (
                                <span className={`team-role-badge ${role}`}>
                                  {role}
                                </span>
                              )}
                              {lastSynced && <span className="team-last-sync">Last synced {lastSynced}</span>}
                            </div>
                          </div>
                          <div className="friend-actions team-actions">
                            {canEditRole && (
                              <select
                                className="team-role-select"
                                value={role}
                                onChange={(e) => handleRoleChange(memberId, e.target.value)}
                              >
                                <option value="owner">Owner</option>
                                <option value="admin">Admin</option>
                                <option value="member">Member</option>
                              </select>
                            )}
                            {canArchive && (
                              <button
                                className="icon-button danger"
                                title="Archive team member"
                                onClick={() => archiveTeamMember(member)}
                                disabled={archivingMemberId === memberId}
                              >
                                <FiUserMinus />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'requests' && (
          <div className="requests-section">
            {loadingRequests ? (
              <div className="loading-state">Loading requests...</div>
            ) : (
              <>
                {/* Incoming Requests */}
                <div className="request-group">
                  <h3>
                    <FiInbox className="inline-icon" />
                    Incoming Requests ({pendingIncomingCount})
                  </h3>
                  {incomingRequests.length === 0 ? (
                    <div className="empty-requests">No incoming requests</div>
                  ) : (
                    <div className="request-list">
                      {incomingRequests.map(request => (
                        <div key={request.id} className="request-card">
                          <div
                            className={`request-avatar ${request.from_profile_photo_key ? 'has-photo' : ''}`}
                            style={request.from_profile_photo_key ? { backgroundImage: `url(/api/profile/photo/${request.from_user_id})` } : undefined}
                          >
                            {!request.from_profile_photo_key && <FiUser />}
                          </div>
                          <div className="request-info">
                            <div className="request-name">
                              {request.from_username || request.from_email.split('@')[0]}
                            </div>
                            <div className="request-email">{request.from_email}</div>
                            <div className="request-time">
                              <FiClock className="inline-icon" />
                              {new Date(request.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="request-actions">
                            <button
                              className="button-primary small"
                              onClick={() => handleRequest(request.id, 'accept')}
                            >
                              <FiCheck /> Accept
                            </button>
                            <button
                              className="button-secondary small"
                              onClick={() => handleRequest(request.id, 'decline')}
                            >
                              <FiX /> Decline
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Outgoing Requests */}
                <div className="request-group">
                  <h3>
                    <FiSend className="inline-icon" />
                    Sent Requests ({pendingOutgoingCount})
                  </h3>
                  {outgoingRequests.length === 0 ? (
                    <div className="empty-requests">No sent requests</div>
                  ) : (
                    <div className="request-list">
                      {outgoingRequests.map(request => (
                        <div key={request.id} className="request-card outgoing">
                          <div
                            className={`request-avatar ${request.to_profile_photo_key ? 'has-photo' : ''}`}
                            style={request.to_profile_photo_key ? { backgroundImage: `url(/api/profile/photo/${request.to_user_id})` } : undefined}
                          >
                            {!request.to_profile_photo_key && <FiUser />}
                          </div>
                          <div className="request-info">
                            <div className="request-name">
                              {request.to_username || request.to_email.split('@')[0]}
                            </div>
                            <div className="request-email">{request.to_email}</div>
                            <div className="request-status">
                              <FiClock className="inline-icon" />
                              Pending since {new Date(request.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="request-actions">
                            <button
                              className="button-secondary small"
                              onClick={() => cancelOutgoingRequest(request.id)}
                            >
                              <FiX /> Cancel
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'add' && (
          <div className="add-friend-section">
            <div className="search-section">
              <h3>Find Friends</h3>
              <p className="muted">Search by username or email address</p>

              <div className="search-box">
                <div className="search-input-wrapper">
                  <input
                    type="text"
                    placeholder="Enter username or email..."
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && searchUser()}
                    className="search-input"
                  />
                  {suggestions.length > 0 && (
                    <div className="suggestions-dropdown">
                      {suggestions.map(user => (
                        <div
                          key={user.id}
                          className="suggestion-item"
                          onClick={() => selectSuggestion(user)}
                        >
                          <div className="suggestion-avatar">
                            <FiUser />
                          </div>
                          <div className="suggestion-info">
                            <span className="suggestion-username">@{user.username}</span>
                            <span className="suggestion-email">{user.email}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={searchUser}
                  disabled={!searchQuery.trim() || loading}
                  className="search-button"
                >
                  {loading ? 'Searching...' : <><FiSearch /> Search</>}
                </button>
              </div>

              {searchResult && (
                <div className="search-results">
                  {searchResult.notFound ? (
                    <div className="no-results">
                      <FiAlertCircle className="alert-icon" />
                      <p>No user found</p>
                      <p className="muted">Try searching by username or email address</p>
                    </div>
                  ) : (
                    <div className="user-result">
                      <div
                        className={`user-avatar ${canShowSearchAvatar ? 'has-photo' : ''}`}
                        style={canShowSearchAvatar ? { backgroundImage: `url(/api/profile/photo/${searchResult.id})` } : undefined}
                      >
                        {!canShowSearchAvatar && <FiUser />}
                      </div>
                      <div className="user-info">
                        <div className="user-name">
                          {searchResult.username ? `@${searchResult.username}` : searchResult.email.split('@')[0]}
                        </div>
                        <div className="user-email">{searchResult.email}</div>
                        {searchResult.isFriend && (
                          <div className="status-badge friend">
                            <FiUserCheck /> Already friends
                          </div>
                        )}
                        {searchResult.hasPendingRequest && (
                          <div className="status-badge pending">
                            <FiClock /> Request pending
                          </div>
                        )}
                        {searchResult.hasIncomingRequest && (
                          <div className="status-badge incoming">
                            <FiInbox /> Has sent you a request
                          </div>
                        )}
                      </div>
                      <div className="user-actions">
                        {!searchResult.isFriend && !searchResult.hasPendingRequest && (
                          <button
                            className="button-primary"
                            onClick={() => sendFriendRequest(searchResult.email)}
                            disabled={sendingRequest}
                          >
                            {sendingRequest ? 'Sending...' : <><FiUserPlus /> Add Friend</>}
                          </button>
                        )}
                        {searchResult.hasPendingRequest && (
                          <button
                            className="button-secondary"
                            onClick={() => cancelOutgoingRequest(getOutgoingRequest(searchResult)?.id)}
                          >
                            <FiX /> Cancel request
                          </button>
                        )}
                        {searchResult.hasIncomingRequest && (
                          <button
                            className="button-primary"
                            onClick={() => setActiveTab('requests')}
                          >
                            View Request
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="info-section">
              <h4>About Friends</h4>
              <ul className="info-list">
                <li>
                  <FiShare2 className="inline-icon" />
                  Share albums privately with friends
                </li>
                <li>
                  <FiUsers className="inline-icon" />
                  Friends can view your shared albums without a public link
                </li>
                <li>
                  <FiSearch className="inline-icon" />
                  Find friends by username or email
                </li>
                <li>
                  <FiUserCheck className="inline-icon" />
                  Both users must accept the friend request
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
